import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../authentication/authentication.service';

export interface AccessContext {
  user_id: string;
  account_state: string;
  account_state_reason: string | null;
  account_blocked: boolean;
  organization_id: string | null;
  organization_role: 'organizational_admin' | 'organization_member' | null;
  license_id: string | null;
  license_type: 'free_trial' | 'freemium' | 'companion' | 'pro' | 'enterprise' | null;
  license_status: 'active' | 'expired' | 'suspended' | 'cancelled' | null;
  license_start: string | null;
  license_end: string | null;
  grace_days: number | null;
  grace_until: string | null;
  effective_license_state: 'active' | 'in_grace' | 'expired' | 'suspended' | 'none';
  seat_ok: boolean;
  calendar_connected: boolean;
  access_allowed: boolean;
  can_schedule_bots: boolean;
  can_create_adhoc_bots: boolean;
  can_send_emails: boolean;
  can_run_ai: boolean;
  can_use_integrations: boolean;
  can_use_crm: boolean;
  can_use_inperson?: boolean;
  can_use_electron?: boolean;
  effective_plan?: string | null;
  meeting_limit?: number | null;
  max_meeting_seconds?: number | null;
  meetings_used_period?: number | null;
  meetings_remaining?: number | null;
  can_start_meeting?: boolean;
}

export interface ActivatedLicense {
  id: string;
  license_type: string;
  status: string;
  start_at: string;
  end_at: string;
  grace_days: number;
}

export interface PendingInvite {
  has_invite: boolean;
  invite_id?: string;
  organization_name?: string;
  organization_id?: string;
  role?: string;
  invited_by_name?: string;
  expires_at?: string;
}

@Injectable({ providedIn: 'root' })
export class LicenseService {
  // ── State ──────────────────────────────────────────────────────────────────
  private _context$ = new BehaviorSubject<AccessContext | null>(null);
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _loadPromise: Promise<void> | null = null;

  readonly context$ = this._context$.asObservable();
  readonly loading$ = this._loading$.asObservable();

  constructor(private readonly _auth: AuthenticationService) {}

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Load (or reload) the access context from the server. De-duplicates concurrent calls. */
  async loadAccessContext(): Promise<void> {
    if (this._loadPromise) return this._loadPromise;
    this._loadPromise = this._fetchAccessContext();
    try {
      await this._loadPromise;
    } finally {
      this._loadPromise = null;
    }
  }

  /** Force refresh — clears cache and reloads. */
  async refreshAccessContext(): Promise<void> {
    this._context$.next(null);
    this._loadPromise = null;
    await this.loadAccessContext();
  }

  getAccessContext(): AccessContext | null {
    return this._context$.getValue();
  }

  // ── Derived helpers ────────────────────────────────────────────────────────

  get effectiveState(): AccessContext['effective_license_state'] {
    return this._context$.getValue()?.effective_license_state ?? 'none';
  }

  get licenseType(): string | null {
    return this._context$.getValue()?.license_type ?? null;
  }

  /** The effective plan the user is on (e.g. 'freemium', 'companion', 'enterprise'). */
  get effectivePlan(): string | null {
    const ctx = this._context$.getValue();
    return ctx?.effective_plan ?? ctx?.license_type ?? null;
  }

  /** True when the user is on the freemium plan. */
  get isFreemium(): boolean {
    return this.effectivePlan === 'freemium';
  }

  /** Meetings allowed in the current period (null = unlimited). */
  get meetingLimit(): number | null {
    return this._context$.getValue()?.meeting_limit ?? null;
  }

  /** Meetings consumed in the current period. */
  get meetingsUsed(): number | null {
    return this._context$.getValue()?.meetings_used_period ?? null;
  }

  /** Meetings remaining in the current period. */
  get meetingsRemaining(): number | null {
    return this._context$.getValue()?.meetings_remaining ?? null;
  }

  get licenseEnd(): Date | null {
    const end = this._context$.getValue()?.license_end;
    return end ? new Date(end) : null;
  }

  /** Days remaining until license expiry (negative = already expired). */
  get daysUntilExpiry(): number | null {
    const end = this.licenseEnd;
    if (!end) return null;
    const ms = end.getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  }

  /** True if license expires within 3 days (and is still technically active/in_grace). */
  get showExpiryWarning(): boolean {
    const days = this.daysUntilExpiry;
    const state = this.effectiveState;
    if (state !== 'active' && state !== 'in_grace') return false;
    return days !== null && days <= 3;
  }

  /** True when the user has no valid access and should see the expired overlay. */
  get isBlocked(): boolean {
    const state = this.effectiveState;
    return state === 'expired' || state === 'none' || state === 'suspended';
  }

  /** True when suspended specifically (different copy from expired). */
  get isSuspended(): boolean {
    const ctx = this._context$.getValue();
    return ctx?.account_state === 'suspended' || ctx?.effective_license_state === 'suspended';
  }

  /** True if the user is an organizational admin. */
  get isOrgAdmin(): boolean {
    return this._context$.getValue()?.organization_role === 'organizational_admin';
  }

  get organizationId(): string | null {
    return this._context$.getValue()?.organization_id ?? null;
  }

  /**
   * True if this user has EVER had a free_trial license.
   * Prevents the "Try free" button from showing again.
   * We detect this from the context: if license_type === 'free_trial'
   * and effective_state is expired/suspended/none — they used it already.
   */
  get hasUsedFreeTrial(): boolean {
    const ctx = this._context$.getValue();
    if (!ctx) return false;
    // If they have any free_trial license (in any state), they've used it
    return ctx.license_type === 'free_trial';
  }

  /** True if user has NO license at all (never activated). */
  get hasNoLicense(): boolean {
    return this._context$.getValue()?.license_id === null;
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /** Activate 14-day free trial. Returns the created license. */
  async activateFreeTrial(): Promise<ActivatedLicense> {
    const token = this._getToken();
    if (!token) throw new Error('No active session');

    const res = await fetch(
      `${environment.apiUrl}/functions/v1/activate-free-trial`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': this._auth.getApiKey,
        },
      }
    );

    const data = await res.json();

    if (!res.ok) {
      if (res.status === 409) {
        throw new Error('trial_already_used');
      }
      throw new Error(data?.error ?? `HTTP ${res.status}`);
    }

    // Refresh context after activation
    await this.refreshAccessContext();
    return data.license as ActivatedLicense;
  }

  /** Check if the current user has any pending invites */
  async checkPendingInvite(): Promise<PendingInvite> {
    const token = this._getToken();
    if (!token) return { has_invite: false };

    try {
      const res = await fetch(
        `${environment.apiUrl}/functions/v1/check-pending-invite`,
        {
          method: 'POST', // or GET depending on edge function, our edge function reads auth header
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'apikey': this._auth.getApiKey,
          },
        }
      );

      if (!res.ok) return { has_invite: false };
      return (await res.json()) as PendingInvite;
    } catch {
      return { has_invite: false };
    }
  }

  /** Accept pending invite (resolves by email automatically) */
  async acceptPendingInvite(): Promise<void> {
    const token = this._getToken();
    if (!token) throw new Error('No active session');

    const res = await fetch(
      `${environment.apiUrl}/functions/v1/accept-org-invite`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': this._auth.getApiKey,
        },
        body: JSON.stringify({}), // No token needed!
      }
    );

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error ?? `HTTP ${res.status}`);

    // Refresh context
    await this.refreshAccessContext();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private async _fetchAccessContext(): Promise<void> {
    const token = this._getToken();
    if (!token) return;

    this._loading$.next(true);
    try {
      // Call PostgREST RPC endpoint directly with the user's JWT
      const url = `${environment.apiUrl}/rest/v1/rpc/get_user_access_context`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'apikey': this._auth.getApiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        console.error('[LicenseService] Failed to fetch access context:', res.status);
        return;
      }

      const data = await res.json();
      // PostgREST returns an array for set-returning functions
      const ctx = Array.isArray(data) ? data[0] : data;
      this._context$.next(ctx ?? null);
    } catch (err) {
      console.error('[LicenseService] Error fetching access context:', err);
    } finally {
      this._loading$.next(false);
    }
  }

  private _getToken(): string | null {
    return this._auth.getUserDetail()?.access_token ?? null;
  }
}
